import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import SectionButtons, { SelectedCommentSection } from './SectionButtons';
import { SectionCommentRowProps } from './SectionCommentRow';
import SectionCommentFooter from './SectionCommentFooter';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FeedbackSection, FeedbackSectionId, UserType } from './utils';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import FeedbackCommentsBySection from './FeedbackCommentsBySection';
import { FeedbackApi, FeedbackStatus } from '../api';
import SubmitReady from 'npo/pages/NPOFeedback/SubmitReady';
import { scrollToTop } from 'shared/utils/pageUtils';
import FeedbackBudget from './FeedbackBudget';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  Answer,
  AnswersBySectionIdResponse,
  getAnswersBySectionId,
} from './api';
import { useClient } from 'shared/apollo';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { CustomQuestion } from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import { DATES_QUESTION_ID_1 } from 'npo/pages/ReportBuilder/Components/ReportFinalize/answersUtil';
import { parseReportPeriod } from 'npo/pages/ReportBuilder/Components/SectionPeriod/utils';
import {
  INDICATOR_DETAILS_QUESTION_ID,
  INDICATOR_SUMMARY_QUESTION_ID,
} from 'npo/pages/NPOFeedback/utils';
import { isReportPassed } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';

const countComments = (sections: SectionCommentRowProps[]): number => {
  let count = 0;
  sections.forEach(s => {
    count += s.comments.length;
  });
  return count;
};

export const fixImpactDetailAnswers = (answers: Answer[]): Answer[] => {
  const firstIndex = answers.findIndex(
    x => x.question.dbId === INDICATOR_DETAILS_QUESTION_ID,
  );
  const secondIndex = answers.findIndex(
    x => x.question.dbId === INDICATOR_SUMMARY_QUESTION_ID,
  );
  if (firstIndex === -1) {
    return answers;
  }
  const secondAnswer: Answer = structuredClone(answers[firstIndex]);
  secondAnswer.questionId = INDICATOR_SUMMARY_QUESTION_ID;
  secondAnswer.question.dbId = INDICATOR_SUMMARY_QUESTION_ID;
  if (secondIndex === -1) {
    answers.push(secondAnswer);
  } else {
    answers[secondIndex] = secondAnswer;
  }
  return answers;
};

const getAllAnswers = (
  sectionId: FeedbackSectionId,
  answers: Answer[] | undefined = [],
  impactSummaryAnswers: Answer[] | undefined = [],
): Answer[] => {
  if (sectionId === 'indicator_details') {
    return [...fixImpactDetailAnswers(answers), ...impactSummaryAnswers];
  }
  return answers;
};

export interface SectionFeedbackProps {
  reportId: number;
  programName: string;
  report: FeedbackApi;
  currentUserId?: string;
  currentUserEmail?: string;
  userName: string;
  userType: UserType;
  allFeedbackSections: FeedbackSection[];
  className?: string;
  feedbackStatus: FeedbackStatus;
  customQuestions: CustomQuestion[];
  reloadReportAnswers?: () => unknown;
  refreshFeedbackStatus: (status: FeedbackStatus) => unknown;
}

const StyledDiv = styled.div`
  background-color: ${COLORS.whiteBase};
  .header-area {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 43px;
    padding-left: 31px;
    padding-right: 31px;
    padding-bottom: 20px;
    border-bottom: 1px solid ${COLORS.grayLightest};
    .menu-button {
      cursor: pointer;
      background-color: inherit;
      border: none;
      outline: none;
      display: flex;
      align-items: center;
      .section-dropdown {
        margin-left: ${SPACERS.tiny};
        .filter_select {
          background-color: ${COLORS.grayLightest};
        }
      }
    }
    .section-heading {
      margin-bottom: 0;
      .down-icon {
        margin-left: ${SPACERS.tiny};
      }
      .heading-light {
        font-weight: 100;
      }
    }
  }
  .feedback-answers-wrapper {
    padding-bottom: ${SPACERS.small};
  }
`;

const SectionFeedback: React.FC<SectionFeedbackProps> = ({
  reportId,
  currentUserId,
  currentUserEmail,
  userName,
  userType,
  customQuestions,
  feedbackStatus,
  allFeedbackSections,
  className,
  report,
  reloadReportAnswers,
  refreshFeedbackStatus,
}) => {
  const client = useClient();
  const [getAnswersRequestState, makeGetAnswersRequest] = useGraphQLRequest<
    AnswersBySectionIdResponse
  >();
  const [
    getImpactSummaryRequestState,
    makeGetImpactSummaryRequest,
  ] = useGraphQLRequest<AnswersBySectionIdResponse>();
  const [getDateRequestState, makeGetDateRequest] = useGraphQLRequest<
    AnswersBySectionIdResponse
  >();
  const [
    allocationAnswersRequestState,
    makeAllocationAnswersRequest,
  ] = useGraphQLRequest<AnswersBySectionIdResponse>();
  const [totalComments, setTotalComments] = useState<number>(0);
  const [showDone, setShowDone] = useState<boolean>(false);
  const [currentSection, setCurrentSection] = useState<FeedbackSection>(
    allFeedbackSections[0],
  );
  const [selectedCommentSection, setSelectedCommentSection] = useState<
    SelectedCommentSection
  >('all');

  useEffect(() => {
    makeGetAnswersRequest(
      getAnswersBySectionId(client!, reportId, currentSection.sectionId),
    );
    if (
      currentSection.sectionId === 'indicator_details' &&
      getImpactSummaryRequestState.type !== 'REQUEST_SUCCESS'
    ) {
      makeGetImpactSummaryRequest(
        getAnswersBySectionId(client!, reportId, 'impact_summary'),
      );
    }
    (getDateRequestState.type === 'REQUEST_INIT' ||
      getDateRequestState.type === 'REQUEST_ERROR') &&
      makeGetDateRequest(getAnswersBySectionId(client!, reportId, 'dates'));
    (allocationAnswersRequestState.type === 'REQUEST_INIT' ||
      allocationAnswersRequestState.type === 'REQUEST_ERROR') &&
      makeAllocationAnswersRequest(
        getAnswersBySectionId(client!, reportId, 'allocation'),
      );
  }, [reportId, currentSection.sectionId]);

  const handleSelectedCommentSection = useCallback(
    (section: SelectedCommentSection) => {
      setSelectedCommentSection(section);
    },
    [setSelectedCommentSection],
  );

  const handleDone = useCallback(() => {
    scrollToTop();
    setShowDone(true);
  }, [setShowDone]);

  const handleSection = useCallback(
    (section: FeedbackSection) => {
      setCurrentSection(section);
    },
    [setCurrentSection],
  );

  if (showDone) {
    return (
      <SubmitReady
        total={totalComments}
        isReady={true}
        reportId={reportId}
        userType={userType}
        isThirdPartyReport={report.cnOptedIn || false}
        feedbackStatus={feedbackStatus}
        progress={{}}
        handleBack={() => setShowDone(false)}
        refreshFeedbackStatus={refreshFeedbackStatus}
        className={className}
      />
    );
  }

  const reportPeriodAnswer = getDateRequestState.data?.allNpoReportAnswers.find(
    x => x.question.dbId === DATES_QUESTION_ID_1,
  );

  if (reportPeriodAnswer) {
    if (reportPeriodAnswer.date1 && reportPeriodAnswer.date2) {
      report.startDate = reportPeriodAnswer.date1;
      report.endDate = reportPeriodAnswer.date2;
    } else if (reportPeriodAnswer.text) {
      const period = parseReportPeriod(reportPeriodAnswer.text);
      if (period.startDate && period.endDate) {
        report.startDate = period.startDate.toISOString().split('T')[0];
        report.endDate = period.endDate.toISOString().split('T')[0];
      }
    }
  }

  const isReportInThePassed = isReportPassed(
    report.endDate,
    Number(report.year),
    report.stage,
  );

  const answersLoading =
    getAnswersRequestState.type === 'REQUEST_INIT' ||
    getAnswersRequestState.type === 'REQUEST_START';
  const isLoading =
    currentSection.sectionId === 'indicator_details'
      ? getImpactSummaryRequestState.type === 'REQUEST_INIT' ||
        getImpactSummaryRequestState.type === 'REQUEST_START' ||
        answersLoading
      : answersLoading;

  return (
    <StyledDiv className={className}>
      <div className="header-area">
        <div className="menu-button">
          <TrueImpactText variant="heading" className="section-heading">
            Section:
          </TrueImpactText>
          <TrueImpactDropdown
            native={false}
            value={currentSection}
            onClear={undefined}
            getId={v => v.sectionId}
            getLabel={v => v.label}
            options={allFeedbackSections}
            onChange={handleSection}
            getSelectorValues={(
              _o: FeedbackSection[],
              value: FeedbackSection | undefined,
            ) => ({
              label: value?.label || 'Select Section',
              isAnySelected: Boolean(value),
            })}
            minWidth="230px"
            className="section-dropdown"
          />
        </div>
        <SectionButtons
          numberOfComments={totalComments}
          selectedSection={selectedCommentSection}
          handleSectionChange={handleSelectedCommentSection}
        />
      </div>
      {isLoading ? (
        <TrueImpactLoading />
      ) : (
        <>
          <FeedbackBudget
            report={report}
            reportId={reportId}
            sectionId={currentSection.sectionId}
            allocationAnswersRequestState={allocationAnswersRequestState}
          />
          {/* <SectionComment
            reportId={reportId}
            sectionId={currentSection.sectionId}
            currentUserId={currentUserId}
            currentUserEmail={currentUserEmail}
            currentUserName={userName}
            currentUserType={userType}
            sectionLevelComments={
              sectionLevelComments[currentSection.sectionId] || []
            }
          /> */}
          <FeedbackCommentsBySection
            isLoading={isLoading}
            reportId={reportId}
            report={report}
            userName={userName}
            userType={userType}
            currentSection={currentSection}
            currentUserId={currentUserId}
            customQuestions={customQuestions}
            currentUserEmail={currentUserEmail}
            isReportInThePassed={isReportInThePassed}
            setTotalComments={setTotalComments}
            reloadReportAnswers={reloadReportAnswers}
            answers={getAllAnswers(
              currentSection.sectionId,
              getAnswersRequestState.data?.allNpoReportAnswers,
              getImpactSummaryRequestState.data?.allNpoReportAnswers,
            )}
            isError={getAnswersRequestState.type === 'REQUEST_ERROR'}
            selectedCommentSection={selectedCommentSection}
            className="feedback-answers-wrapper"
          />
          <SectionCommentFooter
            currentSection={currentSection}
            onDone={handleDone}
            allFeedbackSections={allFeedbackSections}
            setCurrentSection={setCurrentSection}
          />
        </>
      )}
    </StyledDiv>
  );
};

export default SectionFeedback;
